import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { createPortal } from "react-dom";
import { api, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import {
  Lightbulb, ChevronUp, Plus, Trash2, Leaf, Infinity as InfinityIcon, Users, Clock, Euro, MonitorPlay,
} from "lucide-react";

const TRACK_COLOR = { kw_lead: "#22C55E", growth_league: "#E50000" };

function StatusPill({ status, statuses }) {
  const s = statuses.find((x) => x.key === status);
  if (!s) return null;
  return (
    <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border"
      style={{ borderColor: s.color, color: s.color }} data-testid={`idea-status-${status}`}>
      {s.label}
    </span>
  );
}

function IdeaForm({ meta, onDone }) {
  const [params, setParams] = useSearchParams();
  const [open, setOpen] = useState(params.get("nova") === "1");
  const [form, setForm] = useState({
    title: "", description: params.get("texto") || "",
    track: "kw_lead", category: "processos", co: "",
  });
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.post("/eureka/ideas", {
        title: form.title,
        description: form.description,
        track: form.track,
        category: form.category,
        co_author_names: form.co ? form.co.split(",").map((s) => s.trim()).filter(Boolean) : [],
      });
      toast.success("Ideia submetida. Obrigado por contribuir!");
      setForm({ title: "", description: "", track: form.track, category: "processos", co: "" });
      setOpen(false);
      setParams({}, { replace: true });
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button className="kw-btn-primary inline-flex items-center gap-2" onClick={() => setOpen(true)}
        data-testid="eureka-new-idea-btn">
        <Plus className="w-4 h-4" /> Submeter ideia
      </button>
    );
  }

  return (
    <form onSubmit={submit} className="kw-card p-6 space-y-4" data-testid="eureka-idea-form">
      <div className="flex items-center gap-2">
        <Lightbulb className="w-5 h-5 text-[#EAB308]" />
        <h2 className="font-display text-xl font-black uppercase">Nova ideia EUREKA</h2>
      </div>

      <div className="grid md:grid-cols-2 gap-4">
        <div>
          <label className="kw-overline block mb-2">Via</label>
          <select className="kw-input" value={form.track} data-testid="eureka-track-select"
            onChange={(e) => setForm({ ...form, track: e.target.value })}>
            {meta.tracks.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
          </select>
          <div className="text-[10px] uppercase tracking-widest text-[#737373] mt-1">
            {meta.tracks.find((t) => t.key === form.track)?.hint}
          </div>
        </div>
        <div>
          <label className="kw-overline block mb-2">Categoria</label>
          <select className="kw-input" value={form.category} data-testid="eureka-category-select"
            onChange={(e) => setForm({ ...form, category: e.target.value })}>
            {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
        </div>
      </div>

      <div>
        <label className="kw-overline block mb-2">Título</label>
        <input className="kw-input" value={form.title} maxLength={140} required
          placeholder="Numa frase, o que propões?"
          onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="eureka-title-input" />
      </div>

      <div>
        <label className="kw-overline block mb-2">Descrição</label>
        <textarea className="kw-input min-h-[140px]" value={form.description} required
          placeholder="Que problema resolve? Como funcionaria? Que impacto esperas (horas poupadas, € ou pessoas beneficiadas)?"
          onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="eureka-description-input" />
      </div>

      <div>
        <label className="kw-overline block mb-2">Co-autores (opcional)</label>
        <input className="kw-input" value={form.co} placeholder="Nomes separados por vírgula"
          onChange={(e) => setForm({ ...form, co: e.target.value })} data-testid="eureka-coauthors-input" />
      </div>

      <div className="flex gap-3">
        <button className="kw-btn-primary" disabled={saving} data-testid="eureka-submit-btn">
          {saving ? "A submeter…" : "Submeter ideia"}
        </button>
        <button type="button" className="kw-btn-secondary" onClick={() => setOpen(false)}
          data-testid="eureka-cancel-btn">Cancelar</button>
      </div>
    </form>
  );
}

function AdminPanel({ idea, meta, onDone }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    status: idea.status,
    admin_note: idea.admin_note || "",
    hours_saved_month: idea.eroi?.hours_saved_month ?? "",
    euros_year: idea.eroi?.euros_year ?? "",
    people_impacted: idea.eroi?.people_impacted ?? "",
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { status: form.status, admin_note: form.admin_note };
      ["hours_saved_month", "euros_year", "people_impacted"].forEach((k) => {
        if (form[k] !== "" && form[k] !== null) payload[k] = Number(form[k]);
      });
      const { data } = await api.put(`/eureka/ideas/${idea.id}/status`, payload);
      if (data.warning) toast.warning(data.warning);
      else if (form.status === "implemented" && data.white_card_awarded)
        toast.success("Implementada — cartão branco atribuído (80 pts ao clube)");
      else toast.success("Estado actualizado");
      setOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button className="kw-btn-secondary !py-1 !px-3 text-[11px]" onClick={() => setOpen(true)}
        data-testid={`eureka-admin-open-${idea.id}`}>Gerir</button>
    );
  }

  return (
    <div className="mt-4 border-t border-[#222] pt-4 space-y-3" data-testid={`eureka-admin-panel-${idea.id}`}>
      <div className="grid md:grid-cols-4 gap-3">
        <div>
          <label className="kw-overline block mb-2">Estado</label>
          <select className="kw-input" value={form.status} data-testid={`eureka-status-select-${idea.id}`}
            onChange={(e) => setForm({ ...form, status: e.target.value })}>
            {meta.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
          </select>
        </div>
        <div>
          <label className="kw-overline block mb-2">Horas/mês poupadas</label>
          <input type="number" min="0" step="0.5" className="kw-input" value={form.hours_saved_month}
            onChange={(e) => setForm({ ...form, hours_saved_month: e.target.value })}
            data-testid={`eureka-hours-${idea.id}`} />
        </div>
        <div>
          <label className="kw-overline block mb-2">€ / ano</label>
          <input type="number" min="0" step="100" className="kw-input" value={form.euros_year}
            onChange={(e) => setForm({ ...form, euros_year: e.target.value })}
            data-testid={`eureka-euros-${idea.id}`} />
        </div>
        <div>
          <label className="kw-overline block mb-2">Pessoas beneficiadas</label>
          <input type="number" min="0" className="kw-input" value={form.people_impacted}
            onChange={(e) => setForm({ ...form, people_impacted: e.target.value })}
            data-testid={`eureka-people-${idea.id}`} />
        </div>
      </div>
      <div>
        <label className="kw-overline block mb-2">Nota do admin (visível a todos)</label>
        <textarea className="kw-input min-h-[70px]" value={form.admin_note}
          onChange={(e) => setForm({ ...form, admin_note: e.target.value })}
          data-testid={`eureka-note-${idea.id}`} />
      </div>
      {form.status === "implemented" && (
        <p className="text-[11px] uppercase tracking-widest text-[#22C55E]">
          Ao gravar, o autor recebe um cartão branco: 10 pts a cada consultor que coopete no clube {idea.author_club || "—"} (80 ao clube)
        </p>
      )}
      <div className="flex gap-3">
        <button className="kw-btn-primary !py-2" onClick={save} disabled={saving}
          data-testid={`eureka-save-status-${idea.id}`}>{saving ? "A gravar…" : "Gravar"}</button>
        <button className="kw-btn-secondary !py-2" onClick={() => setOpen(false)}>Fechar</button>
      </div>
    </div>
  );
}

function IdeaCard({ idea, meta, isAdmin, onChanged }) {
  const vote = async () => {
    try {
      await api.post(`/eureka/ideas/${idea.id}/vote`);
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const remove = async () => {
    if (!window.confirm("Remover esta ideia?")) return;
    try {
      await api.delete(`/eureka/ideas/${idea.id}`);
      toast.success("Ideia removida");
      onChanged();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  const track = meta.tracks.find((t) => t.key === idea.track);
  const cat = meta.categories.find((c) => c.key === idea.category);
  const canDelete = isAdmin || (idea.is_mine && idea.status === "submitted");

  return (
    <div className="kw-card p-6 flex gap-5" data-testid={`eureka-idea-${idea.id}`}>
      <button onClick={vote} disabled={["implemented", "archived"].includes(idea.status)}
        className={`shrink-0 w-16 flex flex-col items-center justify-center py-3 border transition-colors ${
          idea.has_voted ? "border-[#E50000] bg-[#E50000]/10 text-[#E50000]" : "border-[#2A2A2A] text-[#A3A3A3] hover:border-[#E50000] hover:text-white"
        } disabled:opacity-40`} data-testid={`eureka-vote-${idea.id}`}>
        <ChevronUp className="w-5 h-5" />
        <span className="font-display text-2xl font-black leading-none">{idea.votes_count}</span>
        <span className="text-[9px] uppercase tracking-widest mt-1">votos</span>
      </button>

      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 border"
            style={{ borderColor: TRACK_COLOR[idea.track], color: TRACK_COLOR[idea.track] }}>
            {track?.label}
          </span>
          <span className="text-[10px] uppercase tracking-widest text-[#737373]">{cat?.label}</span>
          <StatusPill status={idea.status} statuses={meta.statuses} />
          {idea.white_card_id && (
            <span className="text-[10px] font-bold uppercase tracking-widest px-2 py-1 bg-white text-black">
              Cartão branco · 80 pts
            </span>
          )}
        </div>

        <h3 className="font-display text-xl font-black uppercase tracking-tight leading-tight">{idea.title}</h3>
        <p className="text-sm text-[#A3A3A3] mt-2 whitespace-pre-wrap">{idea.description}</p>

        {idea.admin_note && (
          <p className="text-xs text-[#EAB308] mt-3 border-l-2 border-[#EAB308] pl-3">{idea.admin_note}</p>
        )}

        {idea.status === "implemented" && (idea.eroi?.hours_saved_month || idea.eroi?.euros_year || idea.eroi?.people_impacted) && (
          <div className="flex gap-4 mt-3 text-[11px] uppercase tracking-widest text-[#22C55E]">
            {!!idea.eroi.hours_saved_month && <span>{idea.eroi.hours_saved_month} h/mês</span>}
            {!!idea.eroi.euros_year && <span>{idea.eroi.euros_year} €/ano</span>}
            {!!idea.eroi.people_impacted && <span>{idea.eroi.people_impacted} pessoas</span>}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 mt-4 flex-wrap">
          <div className="text-[10px] uppercase tracking-widest text-[#737373]">
            {idea.author_name}{idea.author_club ? ` · ${idea.author_club}` : ""}
            {idea.co_author_names?.length > 0 && ` · com ${idea.co_author_names.join(", ")}`}
          </div>
          <div className="flex items-center gap-2">
            {idea.status === "submitted" && (
              <span className="text-[10px] uppercase tracking-widest text-[#555]">
                {Math.max(0, meta.vote_threshold - idea.votes_count)} votos para entrar em análise
              </span>
            )}
            {isAdmin && <AdminPanel idea={idea} meta={meta} onDone={onChanged} />}
            {canDelete && (
              <button onClick={remove} className="text-[#737373] hover:text-[#E50000]"
                data-testid={`eureka-delete-${idea.id}`}><Trash2 className="w-4 h-4" /></button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function ProjectionMode({ ideas, meta, onClose }) {
  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.documentElement.requestFullscreen?.().catch(() => {});
    return () => {
      window.removeEventListener("keydown", onKey);
      if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});
    };
  }, [onClose]);

  const top = ideas.filter((i) => i.status !== "archived").slice(0, 8);

  return createPortal((
    <div className="fixed inset-0 z-[100] bg-[#050505] overflow-y-auto p-10" data-testid="eureka-projection">
      <div className="flex items-start justify-between mb-10">
        <div>
          <div className="kw-overline mb-2">KW Lead · Melhoria contínua</div>
          <h1 className="font-display text-6xl font-black uppercase tracking-tighter">
            EU<span className="text-[#EAB308]">REKA</span>
            <span className="text-[#333] ml-4 text-4xl">ideias mais votadas</span>
          </h1>
        </div>
        <button onClick={onClose} className="kw-btn-secondary" data-testid="eureka-projection-close">
          Sair (Esc)
        </button>
      </div>

      {top.length === 0 ? (
        <div className="font-display text-4xl font-black uppercase text-[#333]">
          Ainda sem ideias — a primeira pode sair desta reunião
        </div>
      ) : (
        <div className="space-y-5">
          {top.map((i, idx) => (
            <div key={i.id} className="flex items-center gap-8 border-b border-[#1A1A1A] pb-5">
              <div className="font-display text-6xl font-black w-20 text-right"
                style={{ color: idx === 0 ? "#EAB308" : idx < 3 ? "#fff" : "#444" }}>
                {String(idx + 1).padStart(2, "0")}
              </div>
              <div className="w-28 text-center">
                <div className="font-display text-5xl font-black text-[#E50000] leading-none">{i.votes_count}</div>
                <div className="text-[10px] uppercase tracking-widest text-[#555] mt-1">votos</div>
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-display text-3xl font-black uppercase tracking-tight leading-tight">{i.title}</div>
                <div className="text-sm text-[#737373] uppercase tracking-widest mt-2">
                  {meta.tracks.find((t) => t.key === i.track)?.label} · {i.author_name}
                  {i.author_club ? ` · ${i.author_club}` : ""} · {meta.statuses.find((s) => s.key === i.status)?.label}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  ), document.body);
}

export default function Eureka() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [meta, setMeta] = useState(null);
  const [ideas, setIdeas] = useState([]);
  const [stats, setStats] = useState(null);
  const [filters, setFilters] = useState({ track: "", status: "", category: "", mine: false, sort: "votes" });
  const [projecting, setProjecting] = useState(false);

  useEffect(() => { api.get("/eureka/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = () => {
    const qs = new URLSearchParams();
    if (filters.track) qs.set("track", filters.track);
    if (filters.status) qs.set("status", filters.status);
    if (filters.category) qs.set("category", filters.category);
    if (filters.mine) qs.set("mine", "true");
    qs.set("sort", filters.sort);
    api.get(`/eureka/ideas?${qs.toString()}`).then((r) => setIdeas(r.data.items)).catch(() => setIdeas([]));
    api.get("/eureka/stats").then((r) => setStats(r.data)).catch(() => {});
  };
  useEffect(load, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!meta) return <div className="kw-overline">A carregar…</div>;

  return (
    <div className="space-y-8 kw-fade-up" data-testid="eureka-page">
      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="kw-overline mb-2">Programa transversal · todos os membros KW Lead</div>
          <h1 className="font-display text-5xl md:text-6xl font-black uppercase tracking-tighter">
            EU<span className="text-[#EAB308]">REKA</span>
          </h1>
          <p className="text-sm text-[#A3A3A3] mt-3 max-w-2xl">
            Melhoria contínua da KW Lead. Qualquer membro — a coopetir, observador ou staff —
            propõe, vota e vê a ideia a ser implementada. Uma ideia implementada vale um
            <strong className="text-white"> cartão branco</strong> e fica no legado do autor.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button className="kw-btn-secondary inline-flex items-center gap-2" onClick={() => setProjecting(true)}
            data-testid="eureka-projection-open">
            <MonitorPlay className="w-4 h-4" /> Modo projeção
          </button>
          <IdeaForm meta={meta} onDone={load} />
        </div>
      </div>

      {projecting && <ProjectionMode ideas={ideas} meta={meta} onClose={() => setProjecting(false)} />}

      {/* e-ROI */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="eureka-eroi">
        {[
          { icon: InfinityIcon, label: "Ideias implementadas", value: stats?.by_status?.implemented ?? 0, color: "#22C55E" },
          { icon: Clock, label: "Horas poupadas / mês", value: stats?.eroi?.hours_saved_month ?? 0, color: "#EAB308" },
          { icon: Euro, label: "e-ROI estimado / ano", value: `${stats?.eroi?.euros_year ?? 0} €`, color: "#E50000" },
          { icon: Users, label: "Pessoas beneficiadas", value: stats?.eroi?.people_impacted ?? 0, color: "#3B82F6" },
        ].map((k) => (
          <div key={k.label} className="kw-card p-5">
            <div className="flex items-center justify-between mb-3">
              <k.icon className="w-5 h-5" style={{ color: k.color }} />
              <div className="kw-overline">{k.label}</div>
            </div>
            <div className="font-display text-3xl font-black leading-none" style={{ color: k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2" data-testid="eureka-filters">
        <button onClick={() => setFilters({ ...filters, track: "" })}
          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-2 border ${!filters.track ? "border-white text-white" : "border-[#2A2A2A] text-[#737373]"}`}
          data-testid="eureka-filter-all">Todas as vias</button>
        {meta.tracks.map((t) => (
          <button key={t.key} onClick={() => setFilters({ ...filters, track: t.key })}
            className={`text-[10px] font-bold uppercase tracking-widest px-3 py-2 border ${filters.track === t.key ? "text-white" : "text-[#737373] border-[#2A2A2A]"}`}
            style={filters.track === t.key ? { borderColor: TRACK_COLOR[t.key], color: TRACK_COLOR[t.key] } : {}}
            data-testid={`eureka-filter-${t.key}`}>{t.label}</button>
        ))}
        <select className="kw-input !py-2 !w-auto" value={filters.status}
          onChange={(e) => setFilters({ ...filters, status: e.target.value })} data-testid="eureka-filter-status">
          <option value="">Todos os estados</option>
          {meta.statuses.map((s) => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select className="kw-input !py-2 !w-auto" value={filters.category}
          onChange={(e) => setFilters({ ...filters, category: e.target.value })} data-testid="eureka-filter-category">
          <option value="">Todas as categorias</option>
          {meta.categories.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
        </select>
        <select className="kw-input !py-2 !w-auto" value={filters.sort}
          onChange={(e) => setFilters({ ...filters, sort: e.target.value })} data-testid="eureka-filter-sort">
          <option value="votes">Mais votadas</option>
          <option value="recent">Mais recentes</option>
        </select>
        <button onClick={() => setFilters({ ...filters, mine: !filters.mine })}
          className={`text-[10px] font-bold uppercase tracking-widest px-3 py-2 border ${filters.mine ? "border-[#E50000] text-[#E50000]" : "border-[#2A2A2A] text-[#737373]"}`}
          data-testid="eureka-filter-mine">As minhas</button>
      </div>

      {ideas.length === 0 ? (
        <div className="kw-card p-10 text-center" data-testid="eureka-empty">
          <Leaf className="w-10 h-10 text-[#2A2A2A] mx-auto mb-4" />
          <div className="font-display text-xl font-black uppercase">Ainda sem ideias por aqui</div>
          <p className="text-sm text-[#737373] mt-2">
            A primeira ideia EUREKA pode ser a tua — leva menos de 2 minutos.
          </p>
        </div>
      ) : (
        <div className="space-y-4" data-testid="eureka-ideas-list">
          {ideas.map((i) => (
            <IdeaCard key={i.id} idea={i} meta={meta} isAdmin={isAdmin} onChanged={load} />
          ))}
        </div>
      )}

      <div className="kw-card p-6" data-testid="eureka-how-it-works">
        <div className="kw-overline mb-3">Como funciona</div>
        <div className="grid md:grid-cols-5 gap-4 text-xs text-[#A3A3A3]">
          {[
            ["1 · Submeter", "Qualquer membro propõe, em duas vias: KW Lead ou Growth League."],
            [`2 · Votar`, `Um voto por pessoa. Com ${meta.vote_threshold} votos a ideia entra automaticamente em análise.`],
            ["3 · Decidir", "A decisão final é do admin, que deixa sempre uma nota visível a todos."],
            ["4 · Implementar", "Ideia implementada = cartão branco: 80 pts ao clube do autor."],
            ["5 · Legado", "Fica para sempre no perfil do autor, com o e-ROI que gerou."],
          ].map(([t, d]) => (
            <div key={t} className="border-l-2 border-[#EAB308] pl-3">
              <div className="font-display text-sm font-black uppercase text-white mb-1">{t}</div>
              {d}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
